import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import api from '../../utils/api';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const ClientUsers = () => {
  const { activeTenant } = useTenant();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'ClientUser',
  });

  const tenantRole = user?.roles?.find((role) => role.tenant_id === activeTenant)?.role;
  const canInvite = tenantRole === 'ClientAdmin';

  const loadUsers = async () => {
    if (!activeTenant) return;
    try {
      setLoading(true);
      const res = await api.get(`/users?tenant_id=${activeTenant}`);
      setUsers(res.data || []);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [activeTenant]);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return users.filter((current) => {
      if (!normalized) return true;
      return (
        current.full_name?.toLowerCase().includes(normalized) ||
        current.email?.toLowerCase().includes(normalized)
      );
    });
  }, [users, query]);

  const handleInvite = async (event) => {
    event.preventDefault();
    if (!activeTenant) return;
    try {
      setInviteLoading(true);
      await api.post('/users/invite-new', {
        ...form,
        tenant_id: activeTenant,
      });
      toast.success('Usuario creado y asignado');
      setForm({ full_name: '', email: '', password: '', role: 'ClientUser' });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al invitar usuario');
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <div data-testid="client-users-page" className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-4xl font-heading font-bold">Colaboradores</h1>
        <p className="text-muted-foreground">Gestiona usuarios del tenant activo</p>
      </div>

      {!canInvite && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700">
          Solo un administrador de cliente puede invitar colaboradores.
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Invitar colaborador</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleInvite} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="invite-name">Nombre</Label>
              <Input
                id="invite-name"
                value={form.full_name}
                onChange={(event) => setForm((prev) => ({ ...prev, full_name: event.target.value }))}
                placeholder="Nombre y apellidos"
                disabled={!canInvite || inviteLoading}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                value={form.email}
                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                placeholder="correo@empresa.com"
                disabled={!canInvite || inviteLoading}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-password">Password temporal</Label>
              <Input
                id="invite-password"
                type="password"
                value={form.password}
                onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
                placeholder="********"
                disabled={!canInvite || inviteLoading}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select
                value={form.role}
                onValueChange={(value) => setForm((prev) => ({ ...prev, role: value }))}
                disabled={!canInvite || inviteLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ClientUser">Colaborador</SelectItem>
                  {tenantRole === 'ClientAdmin' && (
                    <SelectItem value="ClientAdmin">Administrador</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex justify-end">
              <Button type="submit" disabled={!canInvite || inviteLoading}>
                {inviteLoading ? 'Enviando...' : 'Invitar usuario'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <Input
            placeholder="Buscar por nombre o email"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="max-w-xs"
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium">Nombre</th>
                <th className="px-6 py-3 text-left text-sm font-medium">Email</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="2" className="px-6 py-4 text-center">Cargando...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="2" className="px-6 py-4 text-center">No hay usuarios</td></tr>
              ) : (
                filtered.map((current) => (
                  <tr key={current.id} className="border-t">
                    <td className="px-6 py-4">{current.full_name}</td>
                    <td className="px-6 py-4">{current.email}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientUsers;
